import moment from "moment"
import { useQuery } from "react-query"
import { getSystemStatus } from "../../../services/systemStatus"

// 報酬改定202606 セーフティ1 (01-F-05、KIDES 対応 / adult ADULT-293 相当) の判定ロジック。
// adult (packages/adult/containers/Reform202606Alert/useReform202606AlertVisibility.ts)
// と挙動を揃えたもの。kids はデータ構造が異なる (体制等状況管理が service_type 単位の
// facility_system_manage_items 配列) ため取得・抽出処理のみ差し替えている。
//
// 設計書: easyy-docs/reforms/202606/kids/設計/共通_データ移行方針.md §セーフティ1
//
// 表示期間: 2026/6/11 〜 2027/3/31 (令和8年度内)
//   - 開始日 2026-06-11: 本番リリース直後。リリース前に表示しても画面側で新区分の
//     選択肢が出ていないため事業所が切替操作できない、を踏まえてリリース直後表示開始。
//   - 終了日 2027-04-01: 令和9年度開始 = 移行完全完了想定。以降はアラート全体を無効化。
//
// 戻り値:
//   { isVisible: boolean, isPending: boolean }
//     - isVisible: 確定的にアラート対象と判定された (旧コード or 未登録)
//     - isPending: 判定保留中 (表示期間内だがクエリ取得中・エラーで確定判定できていない)
//
//   呼び出し側の使い分け (adult と同じ):
//     - Reform202606Alert (UI バナー): isVisible のみ参照。サイレントフェイル維持
//       (loading/error 時はバナー出さない = 偽陽性アラート防止)。
//     - 請求確定ボタン disabled (将来対応): isVisible || isPending を参照し fail-close。
//
// 2026/6 以降で「有効」と見なす処遇改善加算の区分 (kides 新区分体系):
//   "0"  = 未対象 (加算を取得しない旨を明示登録済み)
//   "41"〜"46" = 令和8年6月 新区分 (Ⅰイ/Ⅰロ/Ⅱイ/Ⅱロ/Ⅲ/Ⅳ)
// これ以外 (旧コード "1"〜"3"/"11"〜"14"/"21"〜"34" や 未登録) はアラート対象。
// バックエンドのセーフティ2/プロシージャ防御 (NOT IN (0,41..46)) と判定を一致させている。

const REFORM_ALERT_START = "2026-06-11"
const REFORM_ALERT_END = "2027-04-01"
const VALID_NURSING_STAFF_VALUES = ["0", "41", "42", "43", "44", "45", "46"]

export type Reform202606AlertVisibility = {
  isVisible: boolean
  isPending: boolean
}

export const useReform202606AlertVisibility = (
  facilityId: number | string
): Reform202606AlertVisibility => {
  const today = moment()
  const isOutOfPeriod =
    today.isBefore(REFORM_ALERT_START) || !today.isBefore(REFORM_ALERT_END)

  const { data, isLoading, isError } = useQuery(
    ["reform-202606-alert", facilityId],
    () => getSystemStatus(String(facilityId), 2026, 6),
    {
      enabled: !!facilityId && !isOutOfPeriod,
      // 複数タブシナリオ対応: 別タブで設定保存後、本タブにフォーカス戻した際に
      // 自動再フェッチでアラートが消える挙動にする。
      refetchOnWindowFocus: true,
      retry: false,
    }
  )

  if (isOutOfPeriod) return { isVisible: false, isPending: false }
  if (!facilityId) return { isVisible: false, isPending: false }
  // クエリ取得中 or エラー: isPending=true。呼び出し側が用途別に扱う。
  if (isLoading || isError) return { isVisible: false, isPending: true }

  const status = data?.data
  const items = status?.facility_system_manage_items ?? []

  // この事業所で請求対象になる service_type (主 + 多機能型の副) のみ判定対象にする。
  // 単一種別の事業所では service_type_id2 = 0 (副なしのセンチネル) が返る。
  // 0 は null/undefined/"" のいずれにも該当せず素通りし、service_type=0 の item が
  // 存在しない → 「未登録」誤判定 → アラート常時表示 + 請求確定ボタン常時 disabled
  // になっていた (単一種別の全事業所で発生)。service_type は 1/2 のみ有効なので
  // Number(v) > 0 で 0 を除外する。
  const targetServiceTypes = [
    status?.service_type,
    status?.service_type_id2,
  ].filter((v) => v !== null && v !== undefined && v !== "" && Number(v) > 0)

  // 対象 service_type の体制が 1 つでも「有効区分」でなければアラート対象。
  // 該当 item が存在しない (= 未登録) 場合も対象に含める。
  const hasInvalid = targetServiceTypes.some((serviceType) => {
    const form = items.find((o) => o?.service_type === serviceType)
    const value =
      form?.welfare_nursing_care_staff_treatment_improvement_addition_target
    return !VALID_NURSING_STAFF_VALUES.includes(String(value ?? ""))
  })

  // 対象 service_type が 1 つも取得できなかった場合 (取得失敗相当) は確定判定を避ける。
  if (targetServiceTypes.length === 0)
    return { isVisible: false, isPending: false }

  return { isVisible: hasInvalid, isPending: false }
}
